<template>
  <div class="email-detail-list">
    <!-- 邮件列表容器 -->
    <div
      class="email-list-container"
      ref="emailListContainer"
      @scroll="handleScroll">
      <!-- 加载状态 -->
      <div v-if="loading && emailList.length === 0" class="loading-container">
        <div class="loading-spinner"></div>
        <div class="loading-text">正在加载邮件...</div>
      </div>

      <!-- 空状态 -->
      <div
        v-else-if="!loading && emailList.length === 0"
        class="empty-container">
        <div class="empty-icon">
          <img src="@/assets/emailIcon.png" alt="" />
        </div>
        <div class="empty-title">暂无邮件记录</div>
        <div class="empty-subtitle">开始发送第一封邮件吧</div>
      </div>

      <!-- 邮件列表 -->
      <div v-else class="email-list">
        <!-- 加载更多状态 -->
        <div v-if="loading && emailList.length > 0" class="load-more-container">
          <div class="loading-spinner small"></div>
          <div class="loading-text">加载更多历史邮件...</div>
        </div>

        <!-- 没有更多数据提示 -->
        <div v-if="!hasMore && emailList.length > 0" class="no-more-container">
          <div class="no-more-text">已加载全部历史邮件</div>
        </div>

        <!-- 邮件项 -->
        <div
          v-for="(email, index) in emailList"
          :key="email.id"
          class="email-item">
          <!-- 左侧头像（对方） -->
          <div v-if="!isCurrentUser(email)" class="photo-container left">
            <img class="photo left" :src="getSenderAvatar(email)" alt="" />
            <div
              class="online-indicator"
              :class="userInfo?.is_online ? 'online' : 'offline'"></div>
          </div>

          <!-- 消息气泡 -->
          <div
            class="message-bubble"
            :class="{ 'is-current-user': isCurrentUser(email) }">
            <!-- 邮件内容 -->
            <div class="email-content" style="position: relative">
              <div v-html="email.content"></div>
              <!-- 翻译功能已隐藏，保留代码以便后续恢复 -->
              <!-- <div
                v-if="email.translatedText"
                style="
                  border-top: 1px solid rgb(220 220 220);
                  padding: 2px 0 0;
                  margin-top: 10px;
                "
                v-html="$highlight(email.translatedText)"></div> -->
              <!-- 翻译功能已隐藏，保留代码以便后续恢复 -->
              <!-- <div style="position: absolute; bottom: -19px; right: 0">
                <img
                  v-if="!email.isTranslating"
                  class="pointer w-18 h-18"
                  :src="
                    email.translatedText ? translatedIcon : untranslatedIcon
                  "
                  alt=""
                  srcset=""
                  @click="handleTranslate(email)" />
                <img
                  v-else
                  class="pointer rotate360 w-18 h-18"
                  src="@/assets/loading.gif"
                  alt=""
                  srcset="" />
              </div> -->
            </div>

            <!-- 附件提示 -->
            <div
              v-if="hasAttachments(email)"
              class="attachment-indicator"
              @click="toggleAttachments(email.id)">
              <img src="@/assets/svg/link.svg" alt="附件" />
              <span>{{ getAttachmentCount(email) }} Files Attached</span>
              <span class="expand-icon">{{
                expandedAttachments[email.id] ? '▼' : '▶'
              }}</span>
            </div>

            <!-- 附件列表 -->
            <div
              v-if="hasAttachments(email) && expandedAttachments[email.id]"
              class="attachment-list">
              <!-- 图片附件 -->
              <div
                v-if="
                  email.attachments?.images &&
                  email.attachments.images.length > 0
                "
                class="attachment-section">
                <div class="section-title">图片</div>
                <div class="attachment-items">
                  <div
                    v-for="(image, index) in email.attachments.images"
                    :key="image.id"
                    class="attachment-item"
                    @click="
                      handleAttachmentClick(email, image, 'image', index)
                    ">
                    <div class="attachment-preview-container">
                      <imagePreview
                        :src="image.url_thumbnail || image.url_original"
                        :previewSrcList="email.attachments.images"
                        :currentIndex="index"
                        class="post-img"
                        :listFields="'url_original'" />
                      <div v-if="image.is_paid" class="seen-overlay">
                        <span class="seen-tag">
                          <img src="@/assets/svg/read.svg" alt="已查看" />
                          seen
                        </span>
                      </div>
                      <div v-else class="seen-overlay">
                        <span class="seen-tag seen-red">
                          <img src="@/assets/svg/unread.svg" alt="未查看" />
                          unseen
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 视频附件 -->
              <div
                v-if="
                  email.attachments?.videos &&
                  email.attachments.videos.length > 0
                "
                class="attachment-section">
                <div class="section-title">视频</div>
                <div class="attachment-items">
                  <div
                    v-for="(video, index) in email.attachments.videos"
                    :key="`video-${video.id}`"
                    class="attachment-item"
                    @click="
                      handleAttachmentClick(email, video, 'video', index)
                    ">
                    <div class="attachment-preview-container">
                      <div class="video-preview">
                        <img
                          :src="video.url_thumbnail"
                          class="attachment-preview"
                          alt=""
                          @click="handleVideoClick(video)" />
                        <div class="video-play-icon">
                          <img src="@/assets/attachment/mttp1.png" alt="播放" />
                        </div>
                      </div>
                      <div v-if="video.is_paid" class="seen-overlay">
                        <span class="seen-tag">
                          <img src="@/assets/svg/read.svg" alt="已查看" />
                          seen
                        </span>
                      </div>
                      <div v-else class="seen-overlay">
                        <span class="seen-tag seen-red">
                          <img src="@/assets/svg/unread.svg" alt="未查看" />
                          unseen
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 时间戳 -->
            <div class="flex row middle between">
              <div class="flex row middle">
                <div
                  :class="
                    'fit-content color-black flex row middle pd-y-2 pd-x-8 br-4 border---gray-70 ' +
                    (email.status === 'read' ? 'bg---green-20' : 'bg---red-20')
                  ">
                  <img
                    class="mr-4 w-16 h-16"
                    :src="
                      email.status === 'read'
                        ? require('@/assets/svg/read.svg')
                        : require('@/assets/svg/unread.svg')
                    " />
                  <span class="fs-12">
                    {{
                      email.status === 'read'
                        ? formatTimeUtils(email.date_read, 8) + ' 已读'
                        : '未读'
                    }}
                  </span>
                </div>
              </div>

              <div class="message-time">
                {{ formatTime(email.date_created) }}
              </div>
            </div>
          </div>

          <!-- 右侧头像（当前用户） -->
          <div v-if="isCurrentUser(email)" class="photo-container right">
            <img class="photo right" :src="getSenderAvatar(email)" alt="" />
            <div class="online-indicator online"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getCorrespondenceEmailsHistory,
  changeEmailStar,
} from '@/api/talkytimes/message';
import ImagePreview from '@/components/imagePreview.vue';
import { translate } from '@/api/youdaoSign';
import { formatTime as formatTimeUtils } from '@/utils/talkytimesFormat';

export default {
  name: 'EmailDetailList',
  components: {
    ImagePreview,
  },
  props: {
    // 聊天用户信息
    chatUserInfo: {
      type: Object,
      required: true,
    },
    emailId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      emailList: [], // 邮件列表
      loading: false, // 加载状态
      hasMore: true, // 是否还有更多数据
      page: 1, // 当前页码
      limit: 20, // 每页数量
      userInfo: null, // 用户信息
      currentUserInfo: null, // 当前用户信息
      scrollPosition: 0, // 滚动位置
      expandedAttachments: {}, // 附件展开状态
      // 翻译相关数据
      translatedIcon: require('@/assets/icon/yFy.png'),
      untranslatedIcon: require('@/assets/icon/nFy.png'),
    };
  },
  mounted() {
    console.log('EmailDetailList 组件已挂载');
    console.log('chatUserInfo:', this.chatUserInfo);
    console.log('emailId:', this.emailId);
    this.initData();
    this.loadEmails();
  },
  watch: {
    chatUserInfo: {
      handler(newVal) {
        console.log('chatUserInfo 发生变化:', newVal);
        if (newVal && Object.keys(newVal).length > 0) {
          this.initData();
          this.loadEmails();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    formatTimeUtils,
    // 初始化数据
    initData() {
      // 从store获取当前用户信息
      this.currentUserInfo = this.$store.state.user.user || {};

      // 从chatUserInfo获取对方用户信息
      this.userInfo = {
        name: this.chatUserInfo.name || this.chatUserInfo.userName,
        avatar_small:
          this.chatUserInfo.avatar_small || this.chatUserInfo.avatar,
        is_online: this.chatUserInfo.is_online || false,
        id: this.chatUserInfo.platformUserId || this.chatUserInfo.userId,
      };

      console.log('初始化数据完成:', {
        currentUserInfo: this.currentUserInfo,
        userInfo: this.userInfo,
        chatUserInfo: this.chatUserInfo,
      });
    },

    // 处理滚动事件
    handleScroll() {
      const container = this.$refs.emailListContainer;
      if (!container || this.loading || !this.hasMore) return;

      const { scrollTop } = container;
      const threshold = 100; // 距离顶部100px时触发加载

      if (scrollTop <= threshold) {
        this.loadMoreEmails();
      }
    },

    // 记录当前滚动位置
    recordScrollPosition() {
      const container = this.$refs.emailListContainer;
      if (container) {
        this.scrollPosition = container.scrollTop;
      }
    },

    // 添加新邮件到列表末尾
    addNewEmail(email) {
      this.emailList.push({
        ...email,
        date_created: this.normalizeTimestamp(email.date_created),
      });

      // 如果新邮件有附件，默认展开
      if (this.hasAttachments(email)) {
        this.$set(this.expandedAttachments, email.id, true);
      }

      // 滚动到底部显示新邮件
      this.$nextTick(() => {
        const container = this.$refs.emailListContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    // 加载邮件列表
    async loadEmails(isLoadMore = false) {
      if (this.loading) return;

      this.loading = true;

      try {
        console.log('this.chatUserInfo:', this.chatUserInfo);
        const params = {
          id_correspondence: this.emailId,
          id_interlocutor: this.chatUserInfo?.idInterlocutor,
          id_user: this.chatUserInfo?.idUser,
          limit: this.limit,
          page: isLoadMore ? this.page + 1 : 1,
        };

        console.log('邮件列表请求参数:', params);
        console.log('chatUserInfo:', this.chatUserInfo);
        console.log('currentUserInfo:', this.currentUserInfo);

        const res = await getCorrespondenceEmailsHistory(params);
        console.log('邮件列表响应:', res);

        if (res && res.data && res.data.history) {
          const newEmails = res.data.history
            .map((email) => ({
              ...email,
              // 确保时间戳格式正确
              date_created: this.normalizeTimestamp(email.date_created),
            }))
            .sort((a, b) => a.date_created - b.date_created); // 按时间正序排列

          console.log('处理后的邮件列表:', newEmails);

          if (isLoadMore) {
            // 向上滚动加载时，新数据添加到前面（更早的邮件）
            this.emailList = [...newEmails, ...this.emailList];
            this.page++;

            // 保持滚动位置，避免页面跳动
            this.$nextTick(() => {
              const container = this.$refs.emailListContainer;
              if (container) {
                // 计算新增内容的高度，调整滚动位置
                const newContentHeight = newEmails.length * 120; // 估算每个邮件项的高度（包含间距）
                container.scrollTop = this.scrollPosition + newContentHeight;
              }
            });
          } else {
            // 初始加载时，确保最新的邮件在底部
            this.emailList = newEmails;
            this.page = 1;

            // 滚动到底部显示最新邮件
            this.$nextTick(() => {
              const container = this.$refs.emailListContainer;
              if (container) {
                container.scrollTop = container.scrollHeight;
              }
            });
          }

          // 默认展开所有有附件的邮件
          this.$nextTick(() => {
            newEmails.forEach((email) => {
              if (this.hasAttachments(email)) {
                this.$set(this.expandedAttachments, email.id, true);
              }
            });
          });

          // 判断是否还有更多数据
          this.hasMore = newEmails.length >= this.limit;
        } else {
          this.hasMore = false;
        }
      } catch (error) {
        console.error('加载邮件列表失败:', error);
        this.$message.error('加载邮件列表失败');
      } finally {
        this.loading = false;
      }
    },

    // 加载更多邮件
    loadMoreEmails() {
      if (this.loading || !this.hasMore) return;

      // 记录当前滚动位置
      this.recordScrollPosition();

      this.loadEmails(true);
    },

    // 获取发送者头像
    getSenderAvatar(email) {
      // 如果是当前用户发送的消息，返回当前用户头像
      if (this.isCurrentUser(email)) {
        const avatar =
          this.chatUserInfo?.platformUserDetail?.avatar_small ||
          require('@/assets/profile.png');
        console.log(
          '当前用户头像:',
          avatar,
          this.chatUserInfo?.platformUserDetail
        );
        return avatar;
      }
      // 如果是对方发送的消息，返回对方头像
      const avatar =
        this.chatUserInfo?.userDetail?.avatar_small ||
        require('@/assets/profile.png');
      console.log('对方头像:', avatar, this.chatUserInfo?.userDetail);
      return avatar;
    },

    // 获取发送者姓名
    getSenderName(email) {
      // 如果是当前用户发送的消息，返回当前用户姓名
      if (this.isCurrentUser(email)) {
        const name =
          this.chatUserInfo?.platformUserDetail?.name ||
          this.chatUserInfo?.platformUserDetail?.username ||
          '我';
        console.log(
          '当前用户姓名:',
          name,
          this.chatUserInfo?.platformUserDetail
        );
        return name;
      }
      // 如果是对方发送的消息，返回对方姓名
      const name =
        this.chatUserInfo?.userDetail?.name ||
        this.chatUserInfo?.userDetail?.username ||
        '对方';
      console.log('对方姓名:', name, this.chatUserInfo?.userDetail);
      return name;
    },

    // 判断是否为当前用户发送的消息
    // 使用请求参数中的id_user和邮件中的id_user_from进行比较
    isCurrentUser(email) {
      // 从请求参数中获取当前用户ID（id_user）
      console.log('currentUserInfo:', email, this.chatUserInfo);
      const currentUserId = this.chatUserInfo?.platformUserId;
      // 从邮件数据中获取发送者ID（id_user_from）
      console.log('emailwujiaoxing:', email, currentUserId);
      const emailSenderId = email.id_user_from;

      console.log('判断邮件归属:', {
        currentUserId,
        emailSenderId,
        isCurrentUser: String(emailSenderId) === String(currentUserId),
        email: email,
      });

      return String(emailSenderId) === String(currentUserId);
    },

    // 检查附件是否已查看
    // 注意：is_paid 字段在此处表示图片是否已被查看，而不是付费状态
    isAttachmentViewed(email, imageId) {
      // 查找对应的图片对象
      const image = email.attachments?.images?.find((img) => img.id == imageId);
      // 使用 is_paid 字段判断是否已查看
      return image ? image.is_paid : false;
    },

    // 标记附件为已查看
    // 注意：is_paid 字段在此处表示图片是否已被查看，而不是付费状态
    markAttachmentAsViewed(email, imageId) {
      // 查找并更新对应的图片对象
      const image = email.attachments?.images?.find((img) => img.id == imageId);
      if (image) {
        image.is_paid = true;
      }
    },

    // 标准化时间戳
    normalizeTimestamp(timestamp) {
      if (!timestamp) return Date.now();

      if (typeof timestamp === 'number') {
        return timestamp;
      }

      if (/^\d+$/.test(timestamp)) {
        return parseInt(timestamp);
      }

      return new Date(
        timestamp.replace(/-/g, '/').replace('T', ' ').replace(/\..*$/, '')
      ).getTime();
    },

    // 格式化日期（用于分割线）
    formatDate(timestamp) {
      const date = new Date(this.normalizeTimestamp(timestamp));
      const now = new Date();
      const currentYear = now.getFullYear();
      const dateYear = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');

      if (dateYear === currentYear) {
        return `${month}-${day}`;
      } else {
        return `${dateYear}-${month}-${day}`;
      }
    },

    // 格式化时间（用于消息气泡）
    formatTime(timestamp) {
      const date = new Date(this.normalizeTimestamp(timestamp));
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const hour = date.getHours().toString().padStart(2, '0');
      const minute = date.getMinutes().toString().padStart(2, '0');

      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      const isThisYear = date.getFullYear() === now.getFullYear();

      if (isToday) {
        return `${hour}:${minute}`;
      } else if (isThisYear) {
        return `${month}-${day} ${hour}:${minute}`;
      } else {
        return `${year}-${month}-${day} ${hour}:${minute}`;
      }
    },

    // 检查是否有附件
    hasAttachments(email) {
      return (
        email.attachments &&
        ((email.attachments.images && email.attachments.images.length > 0) ||
          (email.attachments.videos && email.attachments.videos.length > 0))
      );
    },

    // 获取附件数量
    getAttachmentCount(email) {
      if (!email.attachments) return 0;
      let count = 0;
      if (email.attachments.images) count += email.attachments.images.length;
      if (email.attachments.videos) count += email.attachments.videos.length;
      return count;
    },

    // 切换附件展开状态
    toggleAttachments(emailId) {
      this.$set(
        this.expandedAttachments,
        emailId,
        !this.expandedAttachments[emailId]
      );
    },

    // 处理附件点击
    handleAttachmentClick(email, attachment, type, index) {
      console.log('点击附件:', { email, attachment, type, index });

      // 这里可以添加附件点击的逻辑，比如标记为已查看等
      if (type === 'image') {
        // 图片附件由imagePreview组件处理预览
        console.log('图片附件点击');
      } else if (type === 'video') {
        // 视频附件处理
        console.log('视频附件点击');
      }
    },

    // 处理视频点击
    handleVideoClick(video) {
      console.log('点击视频:', video);
      // 这里可以添加视频播放逻辑
    },

    // 清空邮件列表
    clearEmailList() {
      this.emailList = [];
      this.hasMore = false;
      this.expandedAttachments = {};
      this.emailLimit = 10;
    },

    // 处理翻译
    async handleTranslate(email) {
      if (email.translatedText) {
        return;
      }

      // 设置翻译状态
      this.$set(email, 'isTranslating', true);

      try {
        // 检测语言并翻译
        const languages = this.detectLanguage(email.content);
        let fromLang = '';
        let toLang = '';

        if (languages == 'zh') {
          fromLang = 'zh';
          toLang = 'en';
        } else {
          fromLang = languages;
          toLang = 'zh';
        }

        const translation = await translate(email.content, 'auto', toLang);

        // 设置翻译结果
        this.$set(email, 'translatedText', translation);
        console.log('翻译结果:', translation);
      } catch (err) {
        console.error('翻译失败:', err);
        this.$message.error('翻译失败，请重试');
      } finally {
        // 清除翻译状态
        this.$set(email, 'isTranslating', false);
      }
    },

    // 刷新邮件列表
    refreshEmailList() {
      console.log('刷新邮件列表');
      // 重置分页参数
      this.page = 1;
      this.hasMore = true;
      // 重新加载邮件列表
      this.loadEmails();
    },

    // 检测语言
    detectLanguage(text) {
      if (!text || !text.trim()) return 'unknown';

      // 定义各语言的正则表达式
      const languagePatterns = {
        zh: /[\u4e00-\u9fa5]/, // 中文
        en: /[a-zA-Z]/, // 英文
        th: /[\u0E00-\u0E7F]/, // 泰语
        ja: /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/, // 日语(平假名+片假名+汉字)
        ko: /[\uAC00-\uD7AF]/, // 韩语
        ru: /[\u0400-\u04FF\u0500-\u052F]/, // 俄语
        fr: /[éèêëçàâûîôùÉÈÊËÇÀÂÛÎÔÙ]/, // 法语
      };

      // 计算有效字符总数（排除空格和标点符号）
      const validChars = text.replace(/[\s\p{P}]/gu, '');
      const totalLength = validChars.length;

      // 统计各语言字符数量和计算占比
      const charCounts = {};
      const percentages = {};

      for (const [lang, regex] of Object.entries(languagePatterns)) {
        const matches = validChars.match(new RegExp(regex.source, 'g')) || [];
        charCounts[lang] = matches.length;
        percentages[lang] = (matches.length / totalLength) * 100;
      }

      // 特殊处理日语和中文的汉字重叠问题
      if (percentages.ja > 0 && percentages.zh > 0) {
        const japaneseKana = text.match(/[\u3040-\u309F\u30A0-\u30FF]/g) || [];
        if (japaneseKana.length > 0) {
          // 如果有平假名/片假名，增加日语的权重
          percentages.ja += (japaneseKana.length / totalLength) * 100;
        } else {
          // 如果没有平假名/片假名，则认为是中文
          percentages.ja = 0;
        }
      }

      // 设置语言判断阈值（百分比）
      const THRESHOLD = 60; // 主导语言需要超过60%
      const MINIMUM_THRESHOLD = 30; // 次要语言至少需要30%才考虑

      // 找出占比最高的语言
      let dominantLang = 'unknown';
      let maxPercentage = 0;

      for (const [lang, percentage] of Object.entries(percentages)) {
        if (percentage > maxPercentage) {
          maxPercentage = percentage;
          dominantLang = lang;
        }
      }

      // 如果最高占比的语言超过阈值，则认为是该语言
      if (maxPercentage >= THRESHOLD) {
        return dominantLang;
      }

      // 如果英文占比超过MINIMUM_THRESHOLD，优先判定为英文
      if (percentages.en >= MINIMUM_THRESHOLD) {
        return 'en';
      }

      // 如果没有明显主导语言，但有多种语言混合
      if (maxPercentage > MINIMUM_THRESHOLD) {
        return dominantLang;
      }

      // 默认返回英文
      return 'en';
    },
  },
};
</script>

<style lang="scss" scoped>
.email-detail-list {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: white;
}

.email-list-container {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: white;
  height: calc(100vh - 200px);

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }
}

// 加载状态
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #909399;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e4e7ed;
  border-top: 3px solid #409eff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;

  &.small {
    width: 20px;
    height: 20px;
    border-width: 2px;
    margin-bottom: 8px;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 14px;
  color: #909399;
}

// 空状态
.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #909399;

  .empty-icon {
    margin-bottom: 16px;

    img {
      width: 64px;
      height: 64px;
      opacity: 0.6;
    }
  }

  .empty-title {
    font-size: 16px;
    font-weight: 500;
    color: #606266;
    margin-bottom: 8px;
  }

  .empty-subtitle {
    font-size: 14px;
    color: #909399;
  }
}

// 邮件列表
.email-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 20px;
}

// 时间分割线
.time-divider {
  text-align: center;
  margin: 16px 0;
  font-size: 12px;
  color: #909399;
  position: relative;
  background: #f5f5f5;
  padding: 8px 12px;
  border-radius: 12px;
  display: inline-block;
  margin-left: 50%;
  transform: translateX(-50%);
  border: 1px solid #e4e7ed;
  font-weight: 500;
}

// 邮件项
.email-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  max-width: 80%;
  margin-bottom: 16px;

  &:has(.photo-container.right) {
    flex-direction: row-reverse;
    align-self: flex-end;
  }

  &:has(.photo-container.left) {
    align-self: flex-start;
  }
}

// 头像容器
.photo-container {
  position: relative;
  flex-shrink: 0;

  &.left {
    order: 1;
  }

  &.right {
    order: 1; // 当使用 row-reverse 时，order: 1 会显示在最右边
  }

  .photo {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .online-indicator {
    position: absolute;
    bottom: 2px;
    right: 2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid white;

    &.online {
      background: #67c23a;
    }

    &.offline {
      background: #909399;
    }
  }
}

// 消息气泡
.message-bubble {
  padding: 12px 16px;
  word-wrap: break-word;
  max-width: 100%;
  min-width: 200px;
  order: 2; // 消息气泡始终在中间
  flex: 1;

  &.is-current-user {
    background: white;
    color: #333;
    border-radius: 18px 18px 4px 18px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    .sender-name {
      color: #909399;
    }

    .attachment-indicator {
      color: #909399;
      background: rgba(0, 0, 0, 0.05);

      &:hover {
        background: rgba(0, 0, 0, 0.1);
      }

      img {
        filter: none;
      }
    }

    .seen-tag {
      background: rgba(212, 237, 218, 0.9);
      color: #155724;

      &.seen-red {
        background: rgba(255, 243, 205, 0.9);
        color: #856404;
      }

      img {
        filter: none;
      }
    }

    .message-time {
      color: #909399;
    }
  }

  &:not(.is-current-user) {
    background: white;
    color: #333;
    border-radius: 18px 18px 18px 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.sender-name {
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 4px;
  color: #909399;
}

.email-content {
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 24px;

  ::v-deep {
    img {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
    }

    p {
      margin: 4px 0;
    }
  }
}

.attachment-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #909399;
  margin-bottom: 8px;
  background: rgba(0, 0, 0, 0.05);
  padding: 4px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }

  img {
    width: 14px;
    height: 14px;
  }

  .expand-icon {
    margin-left: auto;
    font-size: 10px;
    transition: transform 0.2s ease;
  }
}

// 附件列表
.attachment-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 8px;

  .attachment-section {
    .section-title {
      font-size: 12px;
      font-weight: 600;
      color: #6c757d;
      margin-bottom: 8px;
      text-transform: uppercase;
    }

    .attachment-items {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;

      .attachment-item {
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        width: 80px;
        height: 80px;

        .attachment-preview-container {
          position: relative;
          width: 80px;
          height: 80px;
          overflow: hidden;
        }

        .attachment-preview {
          width: 100%;
          height: 100%;
          border-radius: 6px;
          object-fit: cover;
        }

        .post-img {
          width: 100%;
          height: 100%;
          border-radius: 6px;
          object-fit: cover;
          cursor: pointer;
        }

        // 确保没有意外的数字显示
        .attachment-preview-container {
          &::before,
          &::after {
            display: none !important;
          }

          // 隐藏任何可能的数字显示
          .preview-image {
            &::before,
            &::after {
              display: none !important;
            }
          }
        }

        .seen-overlay {
          position: absolute;
          top: 4px;
          right: 4px;
          z-index: 2;
          pointer-events: none;
        }

        .seen-tag {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          background: rgba(212, 237, 218, 0.9);
          color: #155724;
          font-size: 9px;
          font-weight: 500;
          padding: 2px 6px;
          border-radius: 8px;
          backdrop-filter: blur(2px);
          pointer-events: none;

          img {
            width: 8px;
            height: 8px;
          }
        }

        .seen-red {
          background: rgba(255, 243, 205, 0.9);
          color: #856404;
        }

        .video-preview {
          position: relative;
          width: 100%;
          height: 100%;

          .attachment-preview {
            width: 100%;
            height: 100%;
            border-radius: 6px;
            object-fit: cover;
          }

          .video-play-icon {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 24px;
            height: 24px;
            background: rgba(0, 0, 0, 0.6);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1;
            pointer-events: none;

            img {
              width: 12px;
              height: 12px;
              filter: brightness(0) invert(1);
            }
          }
        }
      }
    }
  }
}

.message-time {
  font-size: 13px;
  color: #909399;
  text-align: right;
  white-space: nowrap;
}

// 翻译相关样式
.pointer {
  cursor: pointer;
}

.w-18 {
  width: 18px;
}

.h-18 {
  height: 18px;
}

.rotate360 {
  animation: rotate360 1s linear infinite;
}

@keyframes rotate360 {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

// 加载更多状态
.load-more-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  color: #909399;
}

.no-more-container {
  text-align: center;
  padding: 16px;

  .no-more-text {
    font-size: 12px;
    color: #c0c4cc;
  }
}
</style>
